import ApiService from './api.service';

// BE resource REST = `service-orders` (module xưởng dịch vụ — BDSC + Bảo hiểm).
// Response BE chuẩn hóa: { status, statusCode, message, data } — FE đọc `data`.
const BASE_URL = 'service-orders';
const VEHICLE_URL = 'service-vehicles';

/**
 * ServiceOrderService — wrapper quanh BE module "Phiếu dịch vụ".
 * BE controller: backend/src/modules/service-order/controllers/service-order.controller.ts
 *
 * Phase 1: tiếp nhận xe (search/tạo xe+KH/tạo phiếu) + danh sách (filter + summary).
 * Các action nghiệp vụ (báo giá, chuyển bước, MRQ, quyết toán, hoá đơn) bổ sung ở phase sau.
 */
export const ServiceOrderService = {
  /**
   * List phiếu dịch vụ — 6 filter + phân trang + summary 6 nhóm status.
   * @param {Object} params { orderNo, licensePlate, customer, saId, status, fromDate, toDate, page, limit }
   */
  getAll(params) {
    return ApiService.query(BASE_URL, { params });
  },

  /**
   * Detail 1 phiếu dịch vụ.
   * @param {string} id
   */
  getById(id) {
    return ApiService.query(`${BASE_URL}/${id}`);
  },

  /**
   * Tạo phiếu dịch vụ (tiếp nhận xe → QUOTING).
   * @param {Object} data CreateServiceOrderDto { serviceVehicleId, insuranceCompanyId?, surveyorId?, ... }
   */
  create(data) {
    return ApiService.post(BASE_URL, data);
  },

  /**
   * Cập nhật báo giá (SO-002) — không đổi status. Tổng tiền do BE tính lại.
   * @param {string} id
   * @param {Object} data UpdateServiceOrderQuoteDto { items, discount*, ...Section1 }
   */
  updateQuote(id, data) {
    return ApiService.put(`${BASE_URL}/${id}/quote`, data);
  },

  /**
   * Duyệt báo giá (SO-002) — QUOTING/QUOTED → QUOTED.
   * @param {string} id
   */
  approveQuote(id) {
    return ApiService.post(`${BASE_URL}/${id}/approve`, {});
  },

  /**
   * Tiến bước workflow (SO-003) — vd Duyệt lệnh SC (QUOTED → REPAIRING).
   * BE chặn nếu transition không hợp lệ (409) / thuộc phase chưa triển khai (400) /
   * thiếu khách xác nhận (400). State machine BE là nguồn chân lý.
   * @param {string} id
   * @param {Object} payload AdvanceServiceOrderDto { toStatus, customerConfirmed?, note? }
   */
  advance(id, payload) {
    return ApiService.post(`${BASE_URL}/${id}/advance`, payload);
  },

  /**
   * Huỷ phiếu (SO-003) — lý do bắt buộc. BE chặn nếu trạng thái terminal /
   * cần hoàn vật tư-quyết toán (phase sau).
   * @param {string} id
   * @param {string} reason
   */
  cancel(id, reason) {
    return ApiService.post(`${BASE_URL}/${id}/cancel`, { reason });
  },

  /**
   * Lịch sử thao tác phiếu (SO-003 — tab Lịch sử). Trả timeline mới nhất đầu, kèm actor.
   * @param {string} id
   */
  getHistory(id) {
    return ApiService.query(`${BASE_URL}/${id}/history`);
  },

  /**
   * Tìm xe/KH theo biển số / tên / SĐT (bao customers + customer_businesses).
   * @param {string} keyword (≥ 2 ký tự)
   * @param {number} [limit]
   */
  searchVehicle(keyword, limit) {
    return ApiService.query(`${VEHICLE_URL}/search`, {
      params: { keyword, limit },
    });
  },

  /**
   * Tạo xe + chủ xe (modal Thêm KH). Dual-customer theo customerType.
   * @param {Object} data CreateCustomerVehicleDto
   */
  createCustomerVehicle(data) {
    return ApiService.post(VEHICLE_URL, data);
  },

  /**
   * Sửa hồ sơ xe (chỉ thuộc tính xe — số khung, hạn BH, odometer...). KHÔNG đổi chủ xe.
   * @param {string} id service_vehicles.id
   * @param {Object} data UpdateVehicleDto (field null = xoá, vắng = giữ nguyên)
   */
  updateCustomerVehicle(id, data) {
    return ApiService.patch(`${VEHICLE_URL}/${id}`, data);
  },

  // ===== DEMO Phase 5 — Quyết toán nháp (SO-007). CHƯA nghiệp vụ thật (duyệt mô hình). =====

  /**
   * Bắt đầu quyết toán (DEMO): INSPECTED → SETTLING, sinh dòng nháp (LABOR + MATERIAL).
   * @param {string} id
   */
  startSettlement(id) {
    return ApiService.post(`${BASE_URL}/${id}/settlement/start`, {});
  },

  /**
   * Đọc nháp quyết toán + preview 2 bill (gom theo billTarget).
   * @param {string} id
   */
  getSettlement(id) {
    return ApiService.query(`${BASE_URL}/${id}/settlement`);
  },

  /**
   * Cập nhật phân bổ tick-per-dòng (Khách/BH) + tách dòng. Gửi TOÀN BỘ lines.
   * @param {string} id
   * @param {Array} lines SettlementLine[] (đã tick + tách)
   */
  updateSettlementLines(id, lines) {
    return ApiService.put(`${BASE_URL}/${id}/settlement/lines`, { lines });
  },

  /**
   * Duyệt quyết toán: SETTLING → SETTLED. Đóng MRQ + trừ tồn vật tư thực dùng + sinh phiếu XKDV (giá vốn).
   * @param {string} id
   */
  confirmSettlement(id) {
    return ApiService.post(`${BASE_URL}/${id}/settlement/confirm`, {});
  },

  /**
   * Hoàn tác quyết toán: SETTLING → INSPECTED (xoá nháp) hoặc SETTLED → SETTLING
   * (trả tồn + xoá phiếu XKDV + mở lại nháp). Chặn nếu SO đã có hoá đơn.
   * @param {string} id
   */
  undoSettlement(id) {
    return ApiService.post(`${BASE_URL}/${id}/settlement/undo`, {});
  },
};

export default ServiceOrderService;
